import { t, ui, uiError } from '../i18n';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Chip, TextField, Alert
} from '@mui/material';
import {
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon
} from '@mui/icons-material';

interface LoginLog {
  id: number;
  username: string;
  role: string;
  ip_address: string;
  user_agent: string;
  access_type: string;
  screen_resolution: string;
  platform: string;
  language: string;
  location: string;
  created_at: string;
}

interface ActiveSession {
  username: string;
  role: string;
  last_active: string;
  ip_address: string;
  screen_resolution: string;
  user_agent: string;
  access_type: string;
  location: string;
  status: string;
}

const AccessLogs: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(false);

  const PIN_CODE = '950828';

  // 인증 후 15초마다 세션 + 이력 자동 갱신
  useEffect(() => {
    if (!authenticated) return;
    const interval = setInterval(() => {
      loadSessions();
      loadLogs();
    }, 15000);
    return () => clearInterval(interval);
  }, [authenticated]);

  const handlePinSubmit = () => {
    if (pin === PIN_CODE) {
      setAuthenticated(true);
      setPinError('');
      loadLogs();
      loadSessions();
    } else {
      setPinError('Incorrect PIN');
      setPin('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePinSubmit();
  };

  const loadSessions = async () => {
    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/auth/active-sessions`);
      const data = await response.json();
      if (data.success) setSessions(data.data);
    } catch (error) {}
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/auth/login-logs?limit=100`);
      const data = await response.json();
      if (data.success) {
        setLogs(data.data);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const parseUserAgent = (ua: string) => {
    if (!ua) return t('Unknown');
    // In-app browsers (최우선 체크)
    if (ua.includes('KAKAOTALK')) return 'KakaoTalk';
    if (ua.includes('NAVER(')) return 'Naver';
    if (ua.includes('Instagram')) return 'Instagram';
    if (ua.includes('FBAN/') || ua.includes('FBAV/')) return 'Facebook';
    if (ua.includes('Line/')) return 'LINE';
    // iOS browsers (WebKit 기반 — CriOS=Chrome, FxiOS=Firefox, EdgiOS=Edge, OPiOS=Opera)
    if (ua.includes('EdgiOS/')) return 'Edge';
    if (ua.includes('FxiOS/')) return 'Firefox';
    if (ua.includes('OPiOS/')) return 'Opera';
    if (ua.includes('CriOS/')) return 'Chrome';
    // Samsung Internet (UA에 Chrome/ 포함 — Chrome보다 먼저 체크)
    if (ua.includes('SamsungBrowser/')) return 'Samsung';
    // Desktop / Android browsers
    if (ua.includes('Edg/') || ua.includes('Edg ')) return 'Edge';
    if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
    if (ua.includes('Whale/')) return 'Whale';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Chrome/') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Safari/') && !ua.includes('Chrome') && !ua.includes('CriOS')) return 'Safari';
    if (ua.includes('curl/')) return 'curl';
    return t('Other');
  };

  const parseOS = (ua: string, platform?: string) => {
    if (platform) {
      const p = platform.toLowerCase();
      if (p.includes('win')) return 'Windows';
      if (p.includes('mac')) return 'macOS';
      if (p.includes('linux')) return 'Linux';
      if (p.includes('android')) return 'Android';
      if (p.includes('iphone') || p.includes('ipad') || p.includes('ios')) return 'iOS';
    }
    if (!ua) return t('Unknown');
    if (ua.includes('Windows NT 10')) return 'Windows 10/11';
    if (ua.includes('Windows NT')) return 'Windows';
    if (ua.includes('Mac OS X')) return 'macOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Linux')) return 'Linux';
    return t('Other');
  };

  const getLocation = (ip: string, accessType: string) => {
    if (!ip) return '-';
    if (ip === '127.0.0.1' || ip === '::1') return 'Localhost (Inje Univ.)';
    if (ip.startsWith('192.168.0.')) {
      const lastOctet = ip.split('.')[3];
      const base = 'Gimhae, KR';
      switch (lastOctet) {
        case '2': return `${base} - Desktop (Win)`;
        case '3': return `${base} - KULS Desktop0`;
        case '5': return `${base} - KAIST Desktop2`;
        case '24': return `${base} - LAN PC`;
        case '25': return `${base} - LAN PC`;
        case '69': return `${base} - eINS Desktop3`;
        default: return `${base} - LAN (.${lastOctet})`;
      }
    }
    if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.')) return t("Private Network");
    if (accessType === 'External') return t("External Access");
    return ip;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'kuls': return 'primary';
      case 'kaist': return 'secondary';
      case 'eins': return 'warning';
      default: return 'default';
    }
  };

  // PIN 입력 화면
  if (!authenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Card sx={{ maxWidth: 360, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <LockIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>{t("Access Logs")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t("Enter PIN to view login history")}</Typography>
            <TextField
              value={pin}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                if (val.length <= 6) setPin(val);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("000000")}
              type="password"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
              }}
              fullWidth
              autoFocus
              sx={{ mb: 2 }}
            />
            {pinError && <Alert severity="error" sx={{ mb: 2 }}>{uiError(pinError)}</Alert>}
            <Button
              variant="contained"
              fullWidth
              onClick={handlePinSubmit}
              disabled={pin.length !== 6}
            >{t("Unlock")}</Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // 로그 목록 화면
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LoginIcon />{" "}{t("Access Logs")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t("Login history across all accounts")}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadLogs(); loadSessions(); }} disabled={loading}>{t("Refresh")}</Button>
      </Box>

      {/* Currently Online — 상단 고정 상세 테이블 */}
      <Card sx={{ mb: 3, border: '2px solid #4caf50' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4caf50', boxShadow: '0 0 6px #4caf50' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t("Currently Online (")}{sessions.length})
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">{t("Auto-refresh 15s")}</Typography>
          </Box>
          {sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t("No active users")}</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: '#f8fff8' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                    <TableCell><strong>{t("Status")}</strong></TableCell>
                    <TableCell><strong>{t("Username")}</strong></TableCell>
                    <TableCell><strong>{t("Role")}</strong></TableCell>
                    <TableCell><strong>{t("Access")}</strong></TableCell>
                    <TableCell><strong>{t("IP")}</strong></TableCell>
                    <TableCell><strong>{t("Location")}</strong></TableCell>
                    <TableCell><strong>{t("Browser")}</strong></TableCell>
                    <TableCell><strong>{t("OS")}</strong></TableCell>
                    <TableCell><strong>{t("Screen")}</strong></TableCell>
                    <TableCell><strong>{t("Last Active")}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map(session => (
                    <TableRow key={session.username}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50', boxShadow: '0 0 4px #4caf50' }} />
                          <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'bold' }}>{t("ONLINE")}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{session.username}</TableCell>
                      <TableCell>
                        <Chip label={ui(session.role.toUpperCase())} color={getRoleColor(session.role) as any} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={session.access_type || t("LAN")} size="small" variant="outlined" color={session.access_type === 'External' ? 'info' : 'default'} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{session.ip_address}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{session.location || getLocation(session.ip_address, session.access_type || 'LAN')}</TableCell>
                      <TableCell>{parseUserAgent(session.user_agent || '')}</TableCell>
                      <TableCell>{parseOS(session.user_agent || '')}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{session.screen_resolution || '-'}</TableCell>
                      <TableCell>{new Date(session.last_active).toLocaleString('en-US')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip label={t("Total: {0}", [logs.length])} color="default" variant="outlined" />
        <Chip label={t("Admin: {0}", [logs.filter(l => l.role === 'admin').length])} color="error" variant="outlined" />
        <Chip label={t("KULS: {0}", [logs.filter(l => l.role === 'kuls').length])} color="primary" variant="outlined" />
        <Chip label={t("KAIST: {0}", [logs.filter(l => l.role === 'kaist').length])} color="secondary" variant="outlined" />
        <Chip label={t("eINS: {0}", [logs.filter(l => l.role === 'eins').length])} color="warning" variant="outlined" />
        <Chip label={t("External: {0}", [logs.filter(l => l.access_type === 'External').length])} color="info" variant="outlined" />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Alert severity="info">{t("No login records found")}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>{t("Username")}</strong></TableCell>
                <TableCell><strong>{t("Role")}</strong></TableCell>
                <TableCell><strong>{t("Access")}</strong></TableCell>
                <TableCell><strong>{t("IP")}</strong></TableCell>
                <TableCell><strong>{t("Location")}</strong></TableCell>
                <TableCell><strong>{t("Browser")}</strong></TableCell>
                <TableCell><strong>{t("OS")}</strong></TableCell>
                <TableCell><strong>{t("Screen")}</strong></TableCell>
                <TableCell><strong>{t("Time")}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, index) => (
                <TableRow key={log.id} hover>
                  <TableCell>{logs.length - index}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{log.username}</TableCell>
                  <TableCell>
                    <Chip label={ui(log.role.toUpperCase())} color={getRoleColor(log.role) as any} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ui(log.access_type)}
                      size="small"
                      variant="outlined"
                      color={log.access_type === 'External' ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.ip_address}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{log.location || getLocation(log.ip_address, log.access_type)}</TableCell>
                  <TableCell>{parseUserAgent(log.user_agent)}</TableCell>
                  <TableCell>{parseOS(log.user_agent, log.platform)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.screen_resolution || '-'}</TableCell>
                  <TableCell>{new Date(log.created_at).toLocaleString('en-US')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AccessLogs;
